import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CaretRight, ShieldCheck, Stethoscope, ChartLineUp, Clock, Star, GoogleLogo } from "@phosphor-icons/react";
import SectionHeading from "../components/SectionHeading";
import SEO, { ORG_JSON_LD } from "../components/SEO";
import Accreditations from "../components/Accreditations";
import { AdVideo } from "../components/AdVideo";
import { SERVICES } from "../data/services";
import { CONSULTANTS } from "../data/consultants";
import { api } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1759262151080-e05ba1c6294f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2200";

export default function Home() {
  const [reviews, setReviews] = useState([]);
  const [agg, setAgg] = useState(null);
  const [source, setSource] = useState("none");

  useEffect(() => {
    api.get("/reviews").then(({ data }) => {
      if (data?.reviews?.length) {
        setReviews(data.reviews);
        setAgg(data.aggregate || null);
        setSource(data.source || "none");
      }
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title={null}
        description="Private consultant physicians in Cambridge for high blood pressure (hypertension, BP), heart scans (echo), ECG, cholesterol, diabetes, weight loss and heart health checks. Self-pay, self-referral welcome."
        path="/"
        jsonLd={ORG_JSON_LD}
      />
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink/75 via-ink/45 to-ink/10" />
          <span data-testid="hero-image-caption" className="absolute bottom-3 right-4 text-[11px] text-cream-50/85 bg-ink/40 px-2 py-0.5 rounded-sm">Illustrative image, not our premises</span>
        </div>
        <div className="relative container-ccc py-28 lg:py-40 text-cream-50">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="eyebrow text-bronze-light">Cambridge Cardiovascular Prevention Clinic · CUH Affiliated</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.02] max-w-3xl">
              Heart-health care that <em className="text-bronze-light font-serif">actually feels</em> like care.
            </h1>
            <p className="mt-6 max-w-xl text-base lg:text-lg text-cream-100/85 leading-relaxed">
              We’re a specialist Cambridge clinic for people with high blood pressure, raised cholesterol, or worries about their heart — looked after by senior consultants from Cambridge University Hospitals who help write the UK’s national hypertension guidelines.
            </p>
            <p className="mt-3 text-sm text-bronze-light/95 tracking-wide">
              You don’t need a GP referral — most of our patients book themselves in.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link data-testid="hero-enquiry-cta" to="/enquiry" className="btn-primary">Book an appointment</Link>
              <Link data-testid="hero-services-cta" to="/services" className="btn-secondary bg-cream-50/10 border-cream-100/70 text-cream-50 hover:bg-cream-50/20">See how we can help</Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust ribbon */}
      <section className="border-y border-bronze-light/30 bg-cream-100 overflow-hidden">
        <div className="container-ccc py-5 flex flex-wrap items-center justify-center gap-x-16 gap-y-3 text-[11px] uppercase tracking-[0.28em] text-ink-muted font-medium">
          <span>Cambridge University Hospitals</span>
          <span className="text-bronze">•</span>
          <span>Consultant-led care</span>
          <span className="text-bronze">•</span>
          <span>Self-pay patients only</span>
          <span className="text-bronze">•</span>
          <span>Advanced diagnostics</span>
        </div>
      </section>

      {/* Video */}
      <section data-testid="home-video-section" className="container-ccc py-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-4">
          <div className="eyebrow">See how it works</div>
          <h2 className="font-serif text-3xl sm:text-4xl mt-4 leading-[1.1]">From worry to a <em className="font-serif text-bronze-dark">clear plan</em>.</h2>
          <p className="mt-5 text-ink-muted leading-relaxed">Choose your consultant, pick a time and book online in minutes. Tap play to watch (sound on).</p>
          <p className="mt-3 text-xs text-ink-muted">Patient scenes are actor portrayals.</p>
        </div>
        <div className="lg:col-span-8"><AdVideo lang="en" /></div>
      </section>

      {/* Hypertension expertise */}
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-20 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow">Expertise in hypertension</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">
              Deep expertise in <em className="font-serif text-bronze-dark">high blood pressure.</em>
            </h2>
            <p className="mt-6 text-ink-muted text-base lg:text-lg leading-relaxed max-w-2xl">
              Professor Ian Wilkinson, one of our consultants, is Immediate Past-President of the British and Irish Hypertension Society and has contributed to UK hypertension guidance. Our consultants have advised on national cardiovascular prevention work and are active in hypertension research.
            </p>
            <p className="mt-4 text-ink-muted text-base leading-relaxed max-w-2xl">
              You&apos;re seen by consultants whose day-to-day work is high blood pressure and cardiovascular prevention.
            </p>
            <div className="mt-10">
              <div className="eyebrow mb-4">Internationally accredited</div>
              <Accreditations />
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {[
              { v: "30+", t: "Years of expertise in hypertension" },
              { v: "BIHS", t: "Immediate Past-President of British & Irish Hypertension Society" },
              { v: "NHS", t: "Consultant experience within NHS cardiovascular services" },
              { v: "UK", t: "Contributors to UK hypertension guidance" },
            ].map((s) => (
              <div key={s.v} className="p-6 bg-white border border-bronze-light/40 rounded-md">
                <div className="font-serif text-bronze-dark text-3xl">{s.v}</div>
                <div className="text-xs text-ink-muted mt-2 leading-snug">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Intro + Why us */}
      <section className="container-ccc py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="About the clinic"
              title={<>The kind of clinic you <em className="font-serif text-bronze-dark">wish</em> existed.</>}
              subtitle="We help patients whose blood pressure has been hard to control, who’ve been told their cholesterol is high, or who simply want a thorough, expert look at their heart-health risk — without feeling rushed through a 10-minute slot."
            />
            <Link to="/about" data-testid="home-about-link" className="inline-flex items-center gap-2 mt-6 text-bronze-dark font-sans">
              How we work <CaretRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {[
              { icon: <Stethoscope size={26} weight="duotone" />, t: "Always a consultant", d: "Every appointment is with one of our senior consultants — never a trainee." },
              { icon: <ChartLineUp size={26} weight="duotone" />, t: "The right tests", d: "We can arrange the scans, monitors and blood tests you need — only when they’ll genuinely help." },
              { icon: <ShieldCheck size={26} weight="duotone" />, t: "Safe and regulated", d: "Same standards, governance and oversight you’d expect from the NHS — because we live there too." },
              { icon: <Clock size={26} weight="duotone" />, t: "Easy to arrange", d: "Book online or get in touch and we will find a time that suits you." },
            ].map((f) => (
              <div key={f.t} data-testid={`why-us-${f.t.toLowerCase().replace(/\s+/g, "-")}`} className="p-7 bg-white border border-stone-200/60 rounded-md hover:-translate-y-1 hover:shadow-md transition-all">
                <div className="text-bronze-dark">{f.icon}</div>
                <div className="font-serif text-2xl mt-4">{f.t}</div>
                <p className="text-ink-muted mt-2 text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services preview */}
      <section className="bg-cream-100 border-y border-bronze-light/30">
        <div className="container-ccc py-24">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHeading eyebrow="What we help with" title="The things we see most often." />
            <Link data-testid="home-all-services-link" to="/services" className="text-bronze-dark text-sm font-sans">View all services →</Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className="group block p-8 bg-white border border-stone-200/60 rounded-md hover:-translate-y-1 hover:shadow-md transition-all"
              >
                <div className="eyebrow">0{i + 1}</div>
                <h3 className="font-serif text-2xl mt-3">{s.title}</h3>
                <p className="mt-3 text-sm text-ink-muted leading-relaxed">{s.summary}</p>
                <div className="mt-6 text-bronze-dark text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">Read more <CaretRight size={14} /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cardiometabolic feature — weight ↔ blood pressure */}
      <section data-testid="home-cardiometabolic" className="container-ccc py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow">Cardiometabolic medicine · emerging area</div>
            <h2 className="font-serif text-3xl lg:text-4xl mt-4 leading-tight">
              Weight and blood pressure are <em className="text-bronze-dark not-italic">closely linked</em>. Assessing them together is an important part of modern cardiovascular risk reduction.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-ink-muted leading-relaxed">
            <p>
              Weight and blood pressure move together. Evidence shows that even a modest <strong className="text-ink">5–10% reduction in body weight</strong> is associated with meaningful reductions in blood pressure — for some people this can support a reduction in medication doses, always under clinical review. The reverse is also true: unrecognised hypertension and its knock-on effects on insulin resistance make sustained weight change much harder.
            </p>
            <p>
              Cardiometabolic medicine is a <strong className="text-ink">fast-moving area of preventive cardiology</strong>. Recent evidence, including large cardiovascular outcome trials, indicates that assessing weight, blood pressure and metabolic risk <em className="not-italic text-ink">together</em> — rather than in isolation — can be relevant to a person&apos;s long-term risk of heart attack, stroke and heart failure.
            </p>
            <p>
              Our cardiometabolic service assesses weight, blood pressure, insulin resistance, cholesterol, fatty liver and family history in one consultant-led plan — combining practical lifestyle change, targeted blood-pressure and lipid treatment, and, where clinically appropriate following individual assessment, a discussion of the licensed prescription options relevant to your situation.
            </p>
            <div className="pt-3">
              <Link
                to="/services/cardiometabolic-prevention"
                data-testid="home-cardiometabolic-link"
                className="inline-flex items-center gap-2 text-bronze-dark hover:text-bronze font-sans text-sm"
              >
                Learn more about our cardiometabolic clinic <CaretRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Consultants */}
      <section className="container-ccc py-24">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <SectionHeading eyebrow="Our consultants" title="A specialist team." />
          <Link data-testid="home-all-consultants-link" to="/consultants" className="text-bronze-dark text-sm font-sans">Meet the team →</Link>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {CONSULTANTS.map((c) => (
            <Link
              key={c.slug}
              to={`/consultants/${c.slug}`}
              data-testid={`home-consultant-${c.slug}`}
              className="group block bg-white border border-stone-200/60 rounded-md overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all"
            >
              <div className="aspect-[4/5] overflow-hidden bg-cream-100">
                <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="font-serif text-2xl">{c.name}</div>
                <div className="eyebrow mt-2">{c.title}</div>
                <p className="mt-3 text-sm text-ink-muted line-clamp-2">{c.bio}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Patient journey */}
      <section className="relative bg-ink text-cream-50">
        <div className="container-ccc py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="eyebrow text-bronze-light">What to expect</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.08]">From getting in touch to getting answers.</h2>
            <p className="mt-6 text-cream-100/80 leading-relaxed">A simple, friendly process. You get in touch, we book you in, you see one of our consultants, and you leave with a clear plan written in plain English — and a copy goes to your GP.</p>
            <Link data-testid="home-patient-info-link" to="/patient-info" className="inline-block mt-8 btn-primary">What happens at your visit</Link>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { n: "01", t: "Get in touch", d: "Fill in our short form or call us — we will be back with you shortly." },
              { n: "02", t: "Your appointment", d: "A relaxed consultation with your consultant." },
              { n: "03", t: "Tests, if needed", d: "Scans, monitors or blood tests — but only if they’ll actually help." },
              { n: "04", t: "Your plan", d: "A plan that fits your life, written down for you and shared with your GP." },
            ].map((s) => (
              <div key={s.n} className="p-7 border border-cream-100/15 rounded-md">
                <div className="font-serif text-bronze-light text-2xl">{s.n}</div>
                <div className="font-serif text-2xl mt-2">{s.t}</div>
                <p className="text-cream-100/70 mt-2 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews — only renders if real reviews are connected */}
      {reviews.length > 0 && (
        <section data-testid="reviews-section" className="container-ccc py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <SectionHeading eyebrow="Patient reviews" title={<>What our patients <em className="font-serif text-bronze-dark">say</em>.</>} />
            {agg && (
              <div className="flex items-center gap-4 p-5 bg-white border border-stone-200/60 rounded-md">
                <div className="flex items-center gap-1 text-bronze-dark">
                  {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={20} weight="fill" />)}
                </div>
                <div className="text-sm">
                  <div className="font-serif text-2xl text-ink leading-none">{agg.ratingValue}<span className="text-ink-muted text-base">/5</span></div>
                  <div className="eyebrow mt-1">{agg.reviewCount} {source === "google" ? "Google reviews" : "verified reviews"}</div>
                </div>
                {source === "google" && (
                  <a
                    href="https://www.google.com/search?q=Cambridge+Cardiovascular+Clinic+Cambridge"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="reviews-google-link"
                    className="ml-2 inline-flex items-center gap-2 text-sm text-bronze-dark hover:text-bronze-dark/80"
                  >
                    <GoogleLogo size={18} weight="duotone" /> on Google
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {reviews.map((r, i) => {
              const reviewKey = r.id || `${r.author || "anon"}-${(r.body || "").slice(0, 32)}`;
              return (
                <figure
                  key={reviewKey}
                  data-testid={`review-card-${i}`}
                  className="p-7 bg-cream-100 border border-bronze-light/30 rounded-md flex flex-col"
                  itemScope
                  itemType="https://schema.org/Review"
                >
                  <div className="flex items-center gap-1 text-bronze-dark">
                    {[...Array(r.rating || 5)].map((_, k) => <Star key={`${reviewKey}-star-${k}`} size={14} weight="fill" />)}
                  </div>
                  <blockquote className="font-serif text-lg leading-snug mt-4 text-ink flex-1" itemProp="reviewBody">
                    <em>“</em>{r.body}<em>”</em>
                  </blockquote>
                  <figcaption className="mt-5 eyebrow">
                    <span itemProp="author">{r.author}</span>{r.location ? ` · ${r.location}` : ""}
                  </figcaption>
                </figure>
              );
            })}
          </div>

          <div className="mt-14 text-center">
            <Link data-testid="home-final-cta" to="/enquiry" className="btn-primary">Book an appointment</Link>
          </div>
        </section>
      )}
    </div>
  );
}
